import xor from 'lodash/xor';
import { useState } from 'react';

import * as icons from '@creditornot/alchemy-react/icons';
import { TextButton } from '@creditornot/cb-button';
import { Popover } from '@creditornot/cb-popover';
import { Select } from '@creditornot/cb-select';
import { ArrayFilterTag } from 'common/components/cookbook/ArrayFilterTag';
import { BrandsFilter } from 'common/components/cookbook/filters/BrandsFilter/BrandsFilter';
import { useBrandsQuery } from 'common/components/cookbook/filters/BrandsFilter/query';
import { useBrandsFilterTagText } from 'common/components/cookbook/filters/BrandsFilter/useBrandsFilterTagText';
import { VenuesFilter } from 'common/components/cookbook/filters/VenuesFilter/VenuesFilter';
import { PopoverContent } from 'common/components/cookbook/PopoverContent';
import { Stack } from 'common/components/cookbook/Stack';
import { useCountriesQuery } from 'common/hooks/useCountries';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useSetSearch } from 'common/hooks/useSetSearch';
import { useTenantDisplayConfig } from 'common/hooks/useTenantDisplayConfig';
import { useTenantVenueConfig } from 'common/hooks/useTenantVenueConfig';
import { useVenuesQuery } from 'common/hooks/useVenuesQuery';
import { LocalizedMessageId } from 'common/types';
import {
  selectionRecommendationsRouteApi,
  SelectionRecommendationsSearchRouteSchemaType,
} from 'features/selection-recommendations';
import { FilterType } from 'features/selection-recommendations/types';
import { useVendorsQuery } from 'features/vendors/query';

import { VendorFilter } from './VendorFilter';

type Props = {
  filters: FilterType[];
  resetSelectionState: () => void;
  reasonFilter?: React.ReactNode;
};

export const FilterSelect = ({ resetSelectionState, filters, reasonFilter }: Props) => {
  const f = useFormatMessage();
  const [selectedFilter, setSelectedFilter] = useState<FilterType>(filters[0]);
  const countryCodes = useSelectedCountryCodes();
  const { data: countries } = useCountriesQuery();
  const { franchises } = useTenantVenueConfig();
  const { primaryVenueField, secondaryVenueField } = useTenantDisplayConfig(countryCodes[0]);
  const selectedCountryLanguageCode = countries?.find(
    (country) => country.countryCode === countryCodes[0],
  )?.defaultLanguageCode;

  const {
    venueIds = [],
    brandIds = [],
    delistingReasons = [],
    listingReasons = [],
    vendorIds = [],
  } = selectionRecommendationsRouteApi.useSearch();
  const setSearch = useSetSearch<SelectionRecommendationsSearchRouteSchemaType>();

  const options = filters.map((filter) => ({
    label: f(`selection_recommendations.filter:${filter}` as LocalizedMessageId),
    value: filter,
  }));

  const { data: venuesData } = useVenuesQuery({
    countryCodes,
  });

  const { data: vendorsData } = useVendorsQuery({
    countryCodes,
  });

  const { data: brandsData } = useBrandsQuery({
    languageCode: selectedCountryLanguageCode ?? 'en',
    countriesWithActiveProducts: countryCodes,
  });

  const brandsFilterTagText = useBrandsFilterTagText(brandIds);
  const reasons = [...delistingReasons, ...listingReasons];

  const handleChangeVenue = async (venueIds: string[]) => {
    await resetSelectionState();
    setSearch({ venueIds });
  };

  const handleChangeBrand = async (brandIds: string[]) => {
    await resetSelectionState();
    setSearch({ brandIds });
  };

  const handleChangeVendor = async (vendorId: string) => {
    await resetSelectionState();
    setSearch({ vendorIds: xor(vendorIds, [vendorId]) });
  };

  return (
    <Stack direction="row" gap="regularXlarge">
      <Popover
        placement="bottom-start"
        openOnHover={false}
        offset={8}
        renderArrow={false}
        zIndex={1}
        content={
          <PopoverContent $maxWidth={400}>
            <Stack gap="regularMedium">
              <Select
                value={selectedFilter}
                options={options}
                inputId="filter-select"
                onChange={(value) => setSelectedFilter(value as FilterType)}
              />
              {selectedFilter === 'venue' && (
                <VenuesFilter
                  selectedVenues={venueIds}
                  onSelectedVenuesChange={handleChangeVenue}
                  franchises={franchises}
                  title={primaryVenueField}
                  subtitle={secondaryVenueField}
                />
              )}
              {selectedFilter === 'brand' && (
                <BrandsFilter brandIds={brandIds} onBrandChange={handleChangeBrand} />
              )}
              {selectedFilter === 'reason' && reasonFilter}
              {selectedFilter === 'vendor' && (
                <VendorFilter onVendorChange={handleChangeVendor} selectedVendorIds={vendorIds} />
              )}
            </Stack>
          </PopoverContent>
        }
      >
        {({ refProps }) => (
          <TextButton
            {...refProps}
            variant="neutral"
            icon={icons.plus}
            iconPosition="end"
            data-test-id="selection-recommendations-filter"
          >
            {f('common.filter')}
          </TextButton>
        )}
      </Popover>
      <Stack direction="row" gap="regularMedium">
        {venuesData && venueIds.length > 0 && (
          <ArrayFilterTag
            items={venuesData.items
              .filter((venue) => venueIds.includes(venue.id))
              .map((venue) => venue.name)}
            label={f('common.venue')}
            onRemoveClick={() => setSearch({ venueIds: undefined })}
          />
        )}

        {vendorsData && vendorIds.length > 0 && (
          <ArrayFilterTag
            items={vendorsData.items
              .filter((vendor) => vendorIds.includes(vendor.id))
              .map((vendor) => vendor.name)}
            label={f('common.vendor')}
            onRemoveClick={() => setSearch({ vendorIds: undefined })}
          />
        )}
        {reasons.length > 0 && (
          <ArrayFilterTag
            items={reasons.map((reason) =>
              f(`selection_recommendations.reasons:${reason}` as LocalizedMessageId),
            )}
            label={f('selection_recommendations.reason')}
            onRemoveClick={() =>
              setSearch({ delistingReasons: undefined, listingReasons: undefined })
            }
          />
        )}
        {brandsData && brandIds.length > 0 && (
          <ArrayFilterTag
            items={brandsFilterTagText}
            label={f('common.brand')}
            onRemoveClick={() => setSearch({ brandIds: undefined })}
          />
        )}
      </Stack>
    </Stack>
  );
};
